import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input } from 'semantic-ui-react';
import Signature from '../../signature-js/src/Signature';
import { useCroppedBackground } from '../formeditor/useCroppedBackground';
import { FileManagementFileType, uploadFileFromBase64 } from '../helpers/FileUtility';
import { Rgba, getRgbaColorString } from '../helpers/helpers';
import './SignatureWrapper.scss';
import { ClearIcon, FontIcon } from './Icons';
import { BackgroundCropDimensions } from '../types/template-types';
import { SigningMethod } from '../profile/profile-types';

export type SignatureJsValue = {
  Data: unknown[];
  DataUri: string;
  Text: string;
  Font: string;
  // This is added on from the SignatureWrapper
  FileId?: string;
};

export type SignatureJsRef = {
  getValue: () => Promise<SignatureJsValue>;
  hasValue: () => boolean;
  hasChanged: () => boolean;
  clearDrawing: () => void;
};

export type SignatureWrapperProps = {
  maxWidth: number;
  maxHeight: number;
  signingMethod: 0 | 1;
  actualWidth: number;
  actualHeight: number;
  color: string | Rgba;
  defaultText: string;
  defaultFont: string;
  defaultData: unknown;
  formId: string;
  fieldId: string;
  backgroundCropDimensions: BackgroundCropDimensions;
  showLine: boolean;
  maxTextLength: number;
  forceUppercase: boolean;
  readOnly: boolean;
  rowNumber: number;
  selectFontMode: boolean;
  setSelectFontMode: (isFontMode: boolean) => void;
  onSignatureChange: VoidFunc;
  /**
   * HACK: to support IU. TODO - refactor the logic out of this component and into a utility/helper
   */
  onIuSignatureChange: (value: SignatureJsValue, signingMethod: SigningMethod, changed?: boolean) => void;
};

const SignatureWrapper = React.forwardRef<TypeMePlease, TypeMePlease>((props: SignatureWrapperProps, ref: TypeMePlease) => {
  const {
    maxWidth,
    maxHeight,
    signingMethod,
    actualWidth,
    actualHeight,
    color,
    defaultText,
    defaultFont,
    defaultData,
    formId,
    fieldId,
    backgroundCropDimensions,
    showLine,
    maxTextLength,
    forceUppercase,
    readOnly,
    rowNumber,
    selectFontMode,
    setSelectFontMode,
    onSignatureChange,
    onIuSignatureChange
  } = props;
  const [text, setText] = useState(defaultText || '');
  const signatureRef = useRef<SignatureJsRef>();
  const inputRef = useRef<Input>();
  const isIuSignature = !!onIuSignatureChange;

  useEffect(() => {
    ref.current = {
      getValue: async () => {
        const newValue = await signatureRef.current.getValue();

        // HACK: for now, IU only wants the value of the signature,
        //       its endpoints will handle blobs and writing to sql
        if (isIuSignature) {
          return newValue ?? null;
        }

        // TODO - move this client specific logic out into a utility specific for client blobs
        if (!newValue) {
          return null;
        }

        let uploadFieldId = fieldId;
        if (rowNumber !== undefined) {
          uploadFieldId += `_${rowNumber}`;
        }

        const imageUploadResult = await uploadFileFromBase64(
          newValue.DataUri,
          actualWidth,
          actualHeight,
          false,
          true,
          formId,
          FileManagementFileType.Embedded,
          null,
          null,
          null,
          uploadFieldId
        );
        newValue.FileId = imageUploadResult.fileId;
        delete newValue.DataUri;
        return newValue;
      },
      hasChanged: () => {
        return signatureRef.current.hasChanged();
      },
      hasValue: () => {
        return signatureRef.current.hasValue();
      }
    };
  });

  useLayoutEffect(() => {
    if (signingMethod === 1) {
      inputRef.current.focus();
    }
  }, [signingMethod]);

  useEffect(() => {
    // HACK: for now, if the `onIuSignatureChange` event is not null,
    //       call it and return from the wrapper since we want to handle
    //       IU signatures differently than client ones (below)
    if (isIuSignature) {
      signatureRef.current.getValue().then((value) => {
        onIuSignatureChange(value, signingMethod, signatureRef.current.hasChanged());
      });

      return;
    }
    onSignatureChange && onSignatureChange();
  }, [isIuSignature, onIuSignatureChange, onSignatureChange, signingMethod, text]);

  const croppedImg = useCroppedBackground(backgroundCropDimensions);

  const handleClearButtonClicked = () => {
    signatureRef.current.clearDrawing();

    // HACK: for now, if the `onIuSignatureChange` event is not null,
    //       call it and return from the wrapper since we want to handle
    //       IU signatures differently than client ones (below)
    if (isIuSignature) {
      onIuSignatureChange(null, signingMethod, signatureRef.current.hasChanged());

      return;
    }

    onSignatureChange && onSignatureChange();
  };

  // button-wrapper width and canvas-wrapper border
  let displayWidth = maxWidth - (readOnly ? 2 : 28);
  let displayHeight = actualHeight * (displayWidth / actualWidth);
  if (displayHeight > maxHeight) {
    displayHeight = maxHeight;
    displayWidth = actualWidth * (displayHeight / actualHeight);
  }

  return (
    <div className="signature-wrapper">
      <div className="signature-area">
        {signingMethod === 1 ? (
          <div className="text-wrapper">
            <Input
              value={text}
              onChange={(e) => setText(forceUppercase ? e.target.value.toUpperCase() : e.target.value)}
              ref={inputRef}
              maxLength={maxTextLength}
              fluid
              disabled={readOnly}
            />
          </div>
        ) : null}
        <div className={`signature-box${!readOnly ? ' showButton' : ''}`}>
          <Signature
            signingMethod={signingMethod}
            actualWidth={actualWidth}
            actualHeight={actualHeight}
            displayWidth={displayWidth}
            displayHeight={displayHeight}
            text={text}
            defaultFont={defaultFont}
            defaultData={defaultData}
            ref={signatureRef}
            color={getRgbaColorString(color as Rgba)}
            showLine={showLine}
            croppedImg={croppedImg}
            selectFontMode={selectFontMode}
            setSelectFontMode={setSelectFontMode}
            readOnly={readOnly}
            onSignatureChange={
              onSignatureChange ||
              function () {
                return undefined;
              }
            }
          />
          {!readOnly ? (
            <div className="button-wrapper">
              {signingMethod === 0 ? (
                <button onClick={handleClearButtonClicked}>
                  <ClearIcon />
                </button>
              ) : (
                <button onClick={() => setSelectFontMode(true)}>
                  <FontIcon />
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default SignatureWrapper;
